/* eslint-disable import/no-cycle */
import React, { forwardRef } from 'react';
import Dialog from '@material-ui/core/Dialog';
import Modal from '@material-ui/core/Modal';
import IconButton from '@material-ui/core/IconButton';
import Box from '@material-ui/core/Box';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import clsx from 'clsx';
import _get from 'lodash/get';
import useClientTheme from 'components/Hooks/useClientTheme';

// Props:
// - open, onClose, children
// - showCloseButton (default true)
// - CloseIconComponent (optional) -> overrides theme and default
// - classes (optional): forwarded to Dialog; paper will be merged with internal class
// - PaperProps (optional): forwarded to Dialog
// - headerBoxClassName (optional): className for the header Box containing the close button
// - other Dialog props are forwarded

const useDynamicPaperStyles = makeStyles(theme => ({
  paper: props => ({
    // Use !important to override global theme overrides like MuiPaper.root borderRadius: '0 !important'
    borderRadius: `${props.borderRadius}px !important`,
    ...(props.paperPadding != null ? { padding: props.paperPadding } : {}),
    [theme.breakpoints.down('sm')]: {
      borderRadius: `${props.mobileBorderRadius}px !important`,
      ...(props.mobilePaperPadding != null
        ? { padding: props.mobilePaperPadding }
        : {}),
    },
  }),
  topRightPosition: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
}));

const useCloseButtonStyles = makeStyles(theme => ({
  closeButton: props => ({
    cursor: 'pointer',
    '-webkit-tap-highlight-color': 'transparent',
    touchAction: 'manipulation',
    '&:active': {
      opacity: 0.7,
    },
    ...(props.closeTop != null || props.closeRight != null
      ? {
          position: 'absolute',
          ...(props.closeTop != null ? { top: props.closeTop } : {}),
          ...(props.closeRight != null ? { right: props.closeRight } : {}),
        }
      : {}),
    [theme.breakpoints.down('sm')]: {
      ...(props.mobileCloseTop != null || props.mobileCloseRight != null
        ? {
            position: 'absolute',
            ...(props.mobileCloseTop != null
              ? { top: props.mobileCloseTop }
              : {}),
            ...(props.mobileCloseRight != null
              ? { right: props.mobileCloseRight }
              : {}),
          }
        : {}),
    },
  }),
}));

const useWrapperStyles = makeStyles(theme => ({
  div: props => ({
    position: 'absolute',
    top: 0,
    right: 0,
    margin: 32,
    pointerEvents: 'auto',
    boxShadow: theme.shadows[24],
    borderRadius: props.borderRadius,
    ...(props.backgroundColor
      ? {
          backgroundColor: props.backgroundColor,
        }
      : {}),
    '& > div': {
      borderRadius: props.borderRadius,
    },
    [theme.breakpoints.down('sm')]: {
      borderRadius: props.borderRadius,
      '& > div': {
        borderRadius: props.mobileBorderRadius,
      },
    },
    ...(props.closeTop != null || props.closeRight != null
      ? {
          position: 'absolute',
          ...(props.closeTop != null ? { top: props.closeTop } : {}),
          ...(props.closeRight != null ? { right: props.closeRight } : {}),
        }
      : {}),
    [theme.breakpoints.down('sm')]: {
      ...(props.mobileCloseTop != null || props.mobileCloseRight != null
        ? {
            position: 'absolute',
            ...(props.mobileCloseTop != null
              ? { top: props.mobileCloseTop }
              : {}),
            ...(props.mobileCloseRight != null
              ? { right: props.mobileCloseRight }
              : {}),
          }
        : {}),
    },
  }),
}));

const ThemedDialog = forwardRef((props, ref) => {
  const {
    open,
    onClose,
    children,
    showCloseButton = true,
    displayWhiteCloseButton,
    CloseIconComponent: CloseIconOverride,
    classes: externalClasses = {},
    PaperProps: externalPaperProps,
    headerBoxClassName,
    closeButtonClassName,
    position,
    modal,
    ...rest
  } = props;

  const clientDetails = useSelector(makeSelectClientDetails());
  const clientSpecificStyles = _get(
    clientDetails,
    'metadata.altAuthenticationModalStyle',
    {},
  );
  const theme = useClientTheme();
  const borderRadius = _get(theme, 'cmDialog.borderRadius', 5);
  const paperPadding = _get(theme, 'cmDialog.padding');
  const closeTop = _get(theme, 'cmDialog.closeButton.top');
  const closeRight = _get(theme, 'cmDialog.closeButton.right');
  const mobileBorderRadius = _get(theme, 'cmDialogMobile.borderRadius', 5);
  const mobilePaperPadding = _get(theme, 'cmDialogMobile.padding');
  const mobileCloseTop = _get(theme, 'cmDialogMobile.closeButton.top');
  const mobileCloseRight = _get(theme, 'cmDialogMobile.closeButton.right');
  const ThemedCloseIcon =
    CloseIconOverride ||
    _get(theme, [
      'cmDialog',
      displayWhiteCloseButton ? 'closeIconComponentAlt' : 'closeIconComponent',
    ]) ||
    _get(theme, ['cmDialog', 'closeIconComponent']) ||
    CloseIcon;

  const dynamicClasses = useDynamicPaperStyles({
    borderRadius,
    paperPadding,
    mobileBorderRadius,
    mobilePaperPadding,
  });
  const closeButtonClasses = useCloseButtonStyles({
    closeTop,
    closeRight,
    mobileCloseTop,
    mobileCloseRight,
  });
  const wrapperClasses = useWrapperStyles({
    borderRadius,
    mobileBorderRadius,
    backgroundColor: clientSpecificStyles.backgroundColor,
  });

  // Merge paper class names: external + internal dynamic
  const mergedClasses = {
    ...externalClasses,
    paper: clsx(
      externalClasses.paper,
      dynamicClasses.paper,
      position === 'top-right' && dynamicClasses.topRightPosition,
    ),
  };

  const mergedPaperProps = {
    ...externalPaperProps,
    ref,
  };

  const Component = modal ? Modal : Dialog;
  const componentProps = modal
    ? {
        hideBackdrop: true,
        disableBackdropClick: true,
        disableEnforceFocus: true,
        disableScrollLock: true,
        className: mergedClasses.paper,
        style: {
          pointerEvents: 'none',
        },
      }
    : {
        classes: mergedClasses,
        PaperProps: mergedPaperProps,
      };
  const Wrapper = modal ? 'div' : React.Fragment;
  const wrapperProps = modal
    ? {
        className: wrapperClasses.div,
      }
    : {};

  return (
    <Component open={open} onClose={onClose} {...componentProps} {...rest}>
      <Wrapper {...wrapperProps}>
        {showCloseButton && (
          <Box
            className={headerBoxClassName}
            display="flex"
            justifyContent="flex-end"
          >
            <IconButton
              aria-label="close"
              onClick={onClose}
              className={clsx(
                closeButtonClasses.closeButton,
                closeButtonClassName,
              )}
            >
              {typeof ThemedCloseIcon === 'string' ? (
                <img src={ThemedCloseIcon} alt="Close" />
              ) : (
                <ThemedCloseIcon />
              )}
            </IconButton>
          </Box>
        )}
        {children}
      </Wrapper>
    </Component>
  );
});

export default ThemedDialog;
